import React, { useState, useEffect, useCallback, useRef } from "react";
import { Popover, theme, Descriptions } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

const { useToken } = theme;

const alignMobileDeleteInfoPopover = (triggerEl) => {
  if (!triggerEl) return;

  const position = () => {
    const popover = document.querySelector(
      ".billing-delete-info-popover.ant-popover"
    );
    if (!popover) return;

    const rect = triggerEl.getBoundingClientRect();
    const popoverHeight = popover.offsetHeight || 140;
    const gap = 8;
    const viewportPad = 12;
    const spaceAbove = rect.top - viewportPad;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPad;
    const placeAbove =
      spaceAbove >= popoverHeight + gap || spaceBelow < popoverHeight;

    const top = placeAbove
      ? Math.max(viewportPad, rect.top - popoverHeight - gap)
      : Math.min(
          rect.bottom + gap,
          Math.max(viewportPad, window.innerHeight - popoverHeight - viewportPad)
        );

    popover.style.setProperty("position", "fixed", "important");
    popover.style.setProperty("top", `${top}px`, "important");
    popover.style.setProperty("left", "50%", "important");
    popover.style.setProperty("right", "auto", "important");
    popover.style.setProperty("bottom", "auto", "important");
    popover.style.setProperty("transform", "translateX(-50%)", "important");
    popover.style.setProperty(
      "width",
      `min(280px, calc(100vw - ${viewportPad * 2}px))`,
      "important"
    );
    popover.style.setProperty(
      "max-width",
      `calc(100vw - ${viewportPad * 2}px)`,
      "important"
    );
    popover.style.setProperty("margin", "0", "important");
    popover.style.setProperty("z-index", "1600", "important");

    const arrow = popover.querySelector(".ant-popover-arrow");
    if (arrow) {
      arrow.style.setProperty("display", "none", "important");
    }
  };

  requestAnimationFrame(position);
  requestAnimationFrame(() => requestAnimationFrame(position));
};

const CommonPopOver = ({ record }) => {
  const { token } = useToken();
  const [isMobile, setIsMobile] = useState(false);
  const [open, setOpen] = useState(false);
  const contentRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (!isMobile || !open) return;

    alignMobileDeleteInfoPopover(triggerRef.current);

    const closeOnOutsideScroll = (event) => {
      const popoverRoot = contentRef.current?.closest(".ant-popover");
      if (popoverRoot?.contains(event.target)) return;
      setOpen(false);
    };

    window.addEventListener("scroll", closeOnOutsideScroll, true);

    return () => {
      window.removeEventListener("scroll", closeOnOutsideScroll, true);
    };
  }, [isMobile, open]);

  const handleOpenChange = useCallback(
    (nextOpen) => {
      if (isMobile) setOpen(nextOpen);
    },
    [isMobile]
  );

  const items = [
    {
      key: "request_reason",
      label: "Request Reason",
      children:
        record?.service_delete_request_reason ||
        record?.payment_delete_request_reason ||
        "-",
    },
    ...(record?.payment_delete_rejected ||
    record?.service_delete_rejected ||
    record?.accepted_by
      ? [
          {
            key: "by",
            label: record?.accepted_by ? "Accepted By" : "Rejected By",
            children: record?.rejected_by || record?.accepted_by || "-",
          },
          {
            key: "reason",
            label: record?.accepted_reason
              ? "Accepted Reason"
              : "Rejected Reason",
            children:
              record?.rejection_reason || record?.accepted_reason || "-",
          },
        ]
      : []),
  ];

  return (
    <Popover
      open={isMobile ? open : undefined}
      onOpenChange={isMobile ? handleOpenChange : undefined}
      content={
        <div
          ref={contentRef}
          className="billing-delete-info-popover-content"
          style={{
            maxHeight: isMobile ? "120px" : "150px",
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            maxWidth: isMobile ? "100%" : "320px",
            width: isMobile ? "100%" : undefined,
          }}
          onTouchMove={(event) => event.stopPropagation()}
        >
          <Descriptions column={1} size="small" items={items} />
        </div>
      }
      trigger={isMobile ? "click" : "hover"}
      placement={isMobile ? "top" : "bottomLeft"}
      align={
        isMobile
          ? {
              overflow: { adjustX: true, adjustY: true },
            }
          : undefined
      }
      overlayClassName="billing-delete-info-popover"
      getPopupContainer={(trigger) =>
        isMobile
          ? document.body
          : trigger?.closest?.(".historyCard") ||
            trigger?.closest?.(".patient-menu-wrapper") ||
            document.body
      }
    >
      <span
        ref={triggerRef}
        className="billing-delete-info-icon-wrap"
        style={{ display: "inline-flex", alignItems: "center" }}
      >
        <InfoCircleOutlined
          className="billing-delete-info-icon"
          style={{
            color: token.colorPrimary,
            cursor: "pointer",
            fontSize: "14px",
            flexShrink: 0,
          }}
        />
      </span>
    </Popover>
  );
};

export default CommonPopOver;
